import { type ComponentProps, type ReactNode, useContext, useId } from 'react';
import type * as LabelPrimitive from '@radix-ui/react-label';
import { Slot } from '@radix-ui/react-slot';
import {
  FormProvider,
  type FieldValues,
  type FieldPath,
  type ControllerProps,
  Controller,
} from 'react-hook-form';

import {
  FormFieldContext,
  FormItemContext,
  FormReadOnlyContext,
} from '../contexts/form';
import { cn } from '../lib/utils';
import { useFormField } from '../hooks/use-form';
import Label from './label';

const FormRoot = FormProvider;

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

function FormItem({ className, ...props }: ComponentProps<'div'>) {
  const id = useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <div
        className={cn('relative group', className)}
        data-slot="form-item"
        {...props}
      />
    </FormItemContext.Provider>
  );
}

function FormLabel({
  className,
  ...props
}: ComponentProps<typeof LabelPrimitive.Root>) {
  const { error, formItemId } = useFormField();

  return (
    <Label
      className={cn('data-[error=true]:text-destructive', className)}
      data-error={!!error}
      data-slot="form-label"
      htmlFor={formItemId}
      {...props}
    />
  );
}

function FormControl({ className, ...props }: ComponentProps<typeof Slot>) {
  const { error, formItemId, formDescriptionId, formMessageId } =
    useFormField();

  return (
    <Slot
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      className={cn('relative z-10', className)}
      data-slot="form-control"
      id={formItemId}
      {...props}
    />
  );
}

function FormDescription({ className, ...props }: ComponentProps<'p'>) {
  const { formDescriptionId } = useFormField();

  return (
    <p
      className={cn('text-muted-foreground text-xs leading-5', className)}
      data-slot="form-description"
      id={formDescriptionId}
      {...props}
    />
  );
}

function FormMessage({ className, ...props }: ComponentProps<'p'>) {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message ?? '') : props.children;

  if (!body) {
    return null;
  }

  return (
    <p
      className={cn(
        '-mt-1 absolute opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-full rounded-b-md bg-destructive/10 px-2 pt-1 text-[11px] text-destructive leading-5',
        className,
      )}
      data-slot="form-message"
      id={formMessageId}
      {...props}
    >
      {body}
    </p>
  );
}

function FormReadOnly({
  readOnly,
  children,
}: { readOnly?: boolean; children: ReactNode }) {
  const parentReadOnly = useContext(FormReadOnlyContext);
  return (
    <FormReadOnlyContext.Provider value={readOnly ?? parentReadOnly}>
      {children}
    </FormReadOnlyContext.Provider>
  );
}

type FormProps = typeof FormRoot & {
  Field: typeof FormField;
  Item: typeof FormItem;
  Label: typeof FormLabel;
  Control: typeof FormControl;
  Description: typeof FormDescription;
  Message: typeof FormMessage;
  ReadOnly: typeof FormReadOnly;
};

const Form = Object.assign(FormRoot, {
  Field: FormField,
  Item: FormItem,
  Label: FormLabel,
  Control: FormControl,
  Description: FormDescription,
  Message: FormMessage,
  ReadOnly: FormReadOnly,
}) satisfies FormProps;

export default Form;
